import * as React from 'react';
import {
  DataGrid,
  GridColDef,
  GridRowModel,
  GridRowsProp,
  useGridApiRef,
  GridPaginationModel,
  GridFooterContainer,
  GridPagination,
} from '@mui/x-data-grid';
import { itIT } from '@mui/x-data-grid/locales';
import { Box, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Button } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import dayjs, { Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import { formatNumberItalian } from '../utils/numberFormat';
import { downloadCSV, toCSVNumericString } from '../utils/csvExport';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// Base interfaces
export interface BaseEntity {
  id?: string;
  [key: string]: any;
}

export interface BaseData {
  id?: string;
  [key: string]: any;
}

export interface ValueWithEntityReference<TValue extends BaseData = BaseData> {
  value: TValue;
  entityId: string;
  month?: Date | string | Dayjs; // Legacy compatibility
  date?: Date | string | Dayjs; // Generic date reference
  [key: string]: any;
}

type Granularity = 'hour' | 'day' | 'month' | 'year';

export interface ValuePropConfig<TValue extends BaseData = BaseData> {
  field: string;
  label: string;
  isPrimary?: boolean;
  editable?: boolean;
  /** Numero di decimali per la formattazione (es. politica consumi_costi). */
  decimalPlaces?: number;
  getValue?: (valueObj: TValue | null) => number | string | null | undefined;
  setValue?: (
    valueObj: TValue | null,
    newValue: number | string | null,
    entityId?: string,
    date?: Date
  ) => TValue;
}

type ResolvedValueProp = {
  field: string;
  label: string;
  isPrimary: boolean;
  editable: boolean;
  decimalPlaces?: number;
  getValue: (valueObj: BaseData | null) => number | string | null | undefined;
};

const GRANULARITY_LABELS: Record<Granularity, string> = {
  hour: 'Ora',
  day: 'Giorno',
  month: 'Mese',
  year: 'Anno',
};

const GRANULARITY_FORMATS: Record<Granularity, string> = {
  hour: 'HH:00',
  day: 'DD/MM/YYYY',
  month: 'MM/YYYY',
  year: 'YYYY',
};

/** Aggrega più value object (es. per periodo a cavallo d'anno): somma quantity e taxable_amount, unit_cost = sum(taxable)/sum(quantity). */
function aggregateValueObjects(objects: (BaseData | null)[]): BaseData {
  const valid = objects.filter((o): o is BaseData => o != null && typeof o === 'object');
  if (valid.length === 0) return {};
  if (valid.length === 1) return { ...valid[0] };

  const aggregated: BaseData = {};
  let sumQuantity = 0;
  let sumTaxable = 0;

  valid.forEach(o => {
    const q = (o as any).quantity;
    const t = (o as any).taxable_amount ?? (o as any).price;
    if (typeof q === 'number' && !Number.isNaN(q)) sumQuantity += q;
    if (typeof t === 'number' && !Number.isNaN(t)) sumTaxable += t;
  });

  (aggregated as any).quantity = sumQuantity;
  (aggregated as any).taxable_amount = sumTaxable;
  (aggregated as any).price = sumTaxable;
  if (sumQuantity && sumTaxable != null && !Number.isNaN(sumTaxable)) {
    const uc = sumTaxable / sumQuantity;
    (aggregated as any).unit_cost = Number.isFinite(uc) ? uc : null;
  } else {
    const last = valid[valid.length - 1];
    const lastUc = (last as any).unit_cost;
    (aggregated as any).unit_cost =
      lastUc != null && typeof lastUc === 'number' && Number.isFinite(lastUc) ? lastUc : null;
  }
  (aggregated as any).unit_of_measurement = (valid[0] as any).unit_of_measurement;
  (aggregated as any).diagnosis_consumption_id = (valid[0] as any).diagnosis_consumption_id;

  // Contatori e altri: somma il campo generico "value" quando esiste (es. { value: number })
  let sumValue = 0;
  valid.forEach(o => {
    const v = (o as any).value;
    if (typeof v === 'number' && !Number.isNaN(v)) sumValue += v;
  });
  if (valid.some(o => (o as any).value !== undefined)) {
    (aggregated as any).value = sumValue;
  }

  return aggregated;
}

export interface CrossEntityDateCrudDataGridProps<
  TEntity extends BaseEntity = BaseEntity,
  TValue extends BaseData = BaseData,
> {
  granularity?: Granularity; // default: 'month'
  valuesProps?: ValuePropConfig<TValue>[];
  // Period
  periodFrom: Date | string | Dayjs;
  periodTo: Date | string | Dayjs;

  // Entities (columns)
  entities: TEntity[];

  // Values (data points)
  values: ValueWithEntityReference<TValue>[];

  // Value property to display/edit (default: 'value')
  valueProperty?: string;

  // Entity label getter (default: uses entity id or a label property)
  getEntityLabel?: (entity: TEntity) => string;

  // Entity sort key getter (for ordering columns)
  getEntitySortKey?: (entity: TEntity) => number | string | null | undefined;

  // Ordine colonne controllato dall'esterno (field names `entity-{id}`). Se fornito insieme a
  // `onColumnOrderChange`, la griglia diventa "controlled": utile per condividere live lo stesso
  // ordinamento fra più tabelle collegate (es. mensile + annue), evitando che lo spostamento di
  // una colonna sia visibile solo nella tabella su cui è stata trascinata.
  columnOrder?: string[];
  onColumnOrderChange?: (order: string[]) => void;

  // Value getter from the value object
  getValue?: (valueObj: TValue) => number | string | null | undefined;

  // Value setter (for updates)
  setValue?: (valueObj: TValue, newValue: number | string | null) => TValue;

  // Display options
  dateColumnLabel?: string;
  dateColumnHeader?: React.ReactNode; // Custom header for the date column (collapsed state)
  dateColumnHeaderExpanded?: React.ReactNode; // Custom header for the date column (expanded state)
  height?: string | number;
  loading?: boolean;
  readOnly?: boolean; // Se true, le celle non sono editabili
  enablePaste?: boolean; // Se true, abilita la funzione di incolla (default: false)
  pageSize?: number; // Page size per la paginazione (default: nessuna paginazione se hideFooter è true)
  /** Se true, la grid non ha scroll orizzontale proprio; lo scroll è delegato al contenitore (univoco) */
  disableHorizontalScroll?: boolean;
  /** Se true, nasconde la freccia di espansione nell'header della colonna data (es. tabelle annue sotto) */
  disableHeaderExpand?: boolean;
  /** Posizione scroll orizzontale sincronizzata dall'esterno (per allineare più tabelle) */
  syncScrollLeft?: number;
  /** Callback quando l'utente scrolla orizzontalmente (per sincronizzare altre tabelle) */
  onHorizontalScroll?: (scrollLeft: number) => void;

  // Custom renderers
  renderCell?: (params: {
    value: number | string | null | undefined;
    month: Date;
    entityId?: string;
    entity: TEntity;
    valueObj: TValue | null;
    valueProp?: ValuePropConfig<TValue>;
  }) => React.ReactNode;

  // Custom renderer for the "Dato" column (when valuesProps.length > 1)
  renderDataColumnCell?: (params: { row: GridRowModel }) => React.ReactNode;

  // Custom renderer for the date column cell
  renderDateCell?: (params: { value: string; date: Date; row: GridRowModel }) => React.ReactNode;

  // Custom header renderer for entity columns (collapsed state)
  // Receives entity and monthly values array: { month: Date, value: number | string | null }[]
  renderEntityHeader?: (
    entity: TEntity,
    monthlyValues: { month: Date; value: number | string | null }[]
  ) => React.ReactNode;

  // Custom header renderer for entity columns (expanded state)
  // Receives entity and monthly values array: { month: Date, value: number | string | null }[]
  renderEntityHeaderExpanded?: (
    entity: TEntity,
    monthlyValues: { month: Date; value: number | string | null }[]
  ) => React.ReactNode;

  // Column width when using custom header (can be adjusted based on expansion state)
  entityColumnWidth?: number;
  entityColumnWidthExpanded?: number;
  widthCol?: number; // Width for data columns (default: 200)
  widthColDate?: number; // Width for the date column (default: 200)

  // Callback when a cell edit stops (for tracking dirty state)
  onCellEditStop?: () => void;

  // Custom row update processor (for automatic calculations)
  processRowUpdate?: (
    newRow: GridRowModel,
    oldRow: GridRowModel
  ) => GridRowModel | Promise<GridRowModel>;

  // Custom footer component (for date navigation, etc.)
  renderCustomFooter?: () => React.ReactNode;

  // Date navigation props
  dateNavigation?: {
    value: Dayjs | null;
    onChange: (value: Dayjs | null) => void;
  };
}

// Interface for the exposed data structure
export interface CrossEntityTableDataItem {
  month: Date;
  entityId: string;
  value: number | string | Record<string, any> | null;
  valueId?: string | null;
  entity: BaseEntity;
}

// Handle interface for accessing table data
export interface CrossEntityDateCrudDataGridHandle {
  getTableData: () => CrossEntityTableDataItem[];
  importFromClipboard: (
    clipboardData: string,
    startRowIndex?: number,
    startColIndex?: number
  ) => Promise<{ success: boolean; error?: string }>;
  getApiRef: () => any; // Expose apiRef for advanced operations
  goToDate: (date: Date) => void; // Navigate to the page containing the specified date
  getEntityOrder: () => string[]; // Get the current order of entity IDs (columns)
}

/**
 * Riconcilia l'ordine colonne scelto dall'utente (`prevOrder`) con l'elenco "naturale" aggiornato
 * delle entità (`defaultOrder`, es. ordinate per `index` di backend).
 *
 * - Mantiene l'ordine personalizzato per le entità ancora presenti.
 * - Rimuove dall'ordine le entità non più presenti (es. contatore/POD eliminato).
 * - Inserisce le entità nuove (es. nuovo contatore/POD appena creato) vicino alla posizione
 *   del loro predecessore in `defaultOrder`, così non "saltano" in cima o in coda in modo
 *   imprevedibile.
 *
 * Usata per evitare che un cambio nel numero di entità (es. aggiunta di un secondo PODp)
 * invalidi l'intero ordine salvato localmente, rendendo le frecce di spostamento inattive.
 */
export function reconcileColumnOrder(prevOrder: string[], defaultOrder: string[]): string[] {
  const prevSet = new Set(prevOrder);
  const defaultSet = new Set(defaultOrder);

  // Mantieni solo le entità ancora esistenti, preservando l'ordine scelto dall'utente
  const kept = prevOrder.filter(id => defaultSet.has(id));

  if (kept.length === defaultOrder.length) {
    // Nessuna entità aggiunta o rimossa
    return kept;
  }

  const result = [...kept];
  let insertCursor = 0;

  for (const id of defaultOrder) {
    if (prevSet.has(id)) {
      // Entità già presente: aggiorna il cursore sulla sua posizione corrente in `result`
      insertCursor = result.indexOf(id) + 1;
      continue;
    }
    // Entità nuova: inseriscila subito dopo il suo predecessore naturale
    result.splice(insertCursor, 0, id);
    insertCursor += 1;
  }

  return result;
}

/**
 * Cross Entity Date Crud Data Grid
 *
 * A pivot table component where:
 * - Rows: Monthly dates from periodFrom to periodTo
 * - Columns: Entities (first column is the date)
 * - Cells: Editable values
 */
export const CrossEntityDateCrudDataGrid = React.forwardRef<
  CrossEntityDateCrudDataGridHandle,
  CrossEntityDateCrudDataGridProps<any, any>
>(function CrossEntityDateCrudDataGrid(
  {
    granularity = 'month',
    periodFrom,
    periodTo,
    entities,
    values,
    valuesProps,
    valueProperty = 'value',
    getEntityLabel = entity => (entity as any).code || (entity as any).name || entity.id,
    getEntitySortKey,
    columnOrder: controlledColumnOrder,
    onColumnOrderChange,
    getValue = valueObj => (valueObj as any)[valueProperty] ?? null,
    setValue = (valueObj, newValue) => {
      const updated = { ...valueObj };
      (updated as any)[valueProperty] = newValue;
      return updated;
    },
    dateColumnLabel = 'Mese',
    dateColumnHeader,
    dateColumnHeaderExpanded,
    height = 'calc(100vh - 300px)',
    loading = false,
    readOnly = false,
    enablePaste = false,
    pageSize,
    disableHorizontalScroll = false,
    disableHeaderExpand = false,
    syncScrollLeft,
    onHorizontalScroll,
    renderCell,
    renderDataColumnCell,
    renderDateCell,
    renderEntityHeader,
    renderEntityHeaderExpanded,
    entityColumnWidth,
    entityColumnWidthExpanded,
    widthCol = 200, // Default width for data columns
    widthColDate = 200,
    onCellEditStop,
    processRowUpdate,
    renderCustomFooter,
    dateNavigation,
  }: CrossEntityDateCrudDataGridProps<any, any>,
  ref: React.Ref<CrossEntityDateCrudDataGridHandle>
) {
  const apiRef = useGridApiRef();

  // Sync scroll orizzontale: valore assoluto scrollLeft (dove è la barra), non l’evento
  const onHorizontalScrollRef = React.useRef(onHorizontalScroll);
  onHorizontalScrollRef.current = onHorizontalScroll;
  const isApplyingSyncRef = React.useRef(false);

  // Parent dice “mettiti a questa posizione” → imposto scrollLeft
  React.useEffect(() => {
    if (typeof syncScrollLeft !== 'number' || syncScrollLeft < 0) return;
    const api = apiRef.current;
    if (!api?.scroll) return;
    isApplyingSyncRef.current = true;
    api.scroll({ left: syncScrollLeft });
    const t = setTimeout(() => { isApplyingSyncRef.current = false; }, 10);
    return () => clearTimeout(t);
  }, [syncScrollLeft, apiRef]);

  // Internal state for header expansion
  const [isHeaderExpanded, setIsHeaderExpanded] = React.useState(false);

  // State for column order (for column reordering).
  // Se `columnOrder` è passato come prop (vedi `controlledColumnOrder`), la griglia è "controlled":
  // l'ordine è di proprietà del chiamante (che lo condivide tra più tabelle collegate) e qui
  // viene solo letto/propagato tramite `onColumnOrderChange`, senza stato interno duplicato.
  const isColumnOrderControlled = controlledColumnOrder !== undefined;
  const [internalColumnOrder, setInternalColumnOrder] = React.useState<string[]>([]);
  const columnOrder = isColumnOrderControlled ? controlledColumnOrder! : internalColumnOrder;
  const updateColumnOrder = React.useCallback(
    (updater: string[] | ((prev: string[]) => string[])) => {
      if (isColumnOrderControlled) {
        const prev = controlledColumnOrder ?? [];
        const next = typeof updater === 'function' ? (updater as (p: string[]) => string[])(prev) : updater;
        onColumnOrderChange?.(next);
      } else {
        setInternalColumnOrder(updater);
      }
    },
    [isColumnOrderControlled, controlledColumnOrder, onColumnOrderChange]
  );

  // Leggo il valore assoluto scrollLeft e lo notifico (niente rAF: uso il valore nel momento dell’evento)
  React.useEffect(() => {
    if (!onHorizontalScrollRef.current) return;
    let scrollEl: HTMLElement | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const handler = () => {
      if (isApplyingSyncRef.current || !scrollEl) return;
      const pos = scrollEl.scrollLeft;
      onHorizontalScrollRef.current?.(pos);
    };

    const getScrollElement = (): HTMLElement | null => {
      const api = apiRef.current as { virtualScrollerRef?: React.RefObject<HTMLElement | null>; rootElementRef?: React.RefObject<HTMLElement | null> } | null;
      if (api?.virtualScrollerRef?.current instanceof HTMLElement) return api.virtualScrollerRef.current;
      const q = api?.rootElementRef?.current?.querySelector('.MuiDataGrid-virtualScroller');
      return q instanceof HTMLElement ? q : null;
    };

    const attach = (): boolean => {
      const el = getScrollElement();
      if (!el) return false;
      scrollEl = el;
      el.addEventListener('scroll', handler, { passive: true });
      return true;
    };

    if (!attach()) {
      let n = 0;
      intervalId = setInterval(() => {
        if (attach() || ++n >= 80) clearInterval(intervalId!);
      }, 10);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      scrollEl?.removeEventListener('scroll', handler);
    };
  }, [apiRef]);

  // State for pagination
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>(
    pageSize ? { page: 0, pageSize } : { page: 0, pageSize: 100 }
  );

  // Sincronizza la data quando cambia la paginazione
  React.useEffect(() => {
    if (pageSize && dateNavigation && periodFrom) {
      // Calcola la data basata sulla pagina corrente
      const startDateObj = periodFrom instanceof Date ? periodFrom : dayjs(periodFrom).toDate();
      const startDate = new Date(
        startDateObj.getFullYear(),
        startDateObj.getMonth(),
        startDateObj.getDate()
      );
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + paginationModel.page);
      const newDate = dayjs(currentDate);
      // Aggiorna solo se la data è diversa per evitare loop infiniti
      if (!dateNavigation.value || !dateNavigation.value.isSame(newDate, 'day')) {
        dateNavigation.onChange(newDate);
      }
    }
  }, [paginationModel.page, pageSize, dateNavigation, periodFrom]);

  // Context menu state
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
    rowId: string;
    field: string;
    rowIndex: number;
    colIndex: number;
  } | null>(null);

  // Mini line chart component (sparkline) - always visible below entity header
  const MiniLineChart = React.useCallback(
    ({
      values,
      width = 60,
      height = 20,
    }: {
      values: { month: Date; value: number | string | null }[];
      width?: number;
      height?: number;
    }) => {
      // Filtra valori validi (numerici)
      const validValues = values
        .map(v => {
          const numValue = typeof v.value === 'number' ? v.value : Number(v.value);
          return isNaN(numValue) ? null : numValue;
        })
        .filter(v => v !== null) as number[];

      if (validValues.length === 0) {
        return null;
      }

      // Calcola min e max per la scala
      const min = Math.min(...validValues);
      const max = Math.max(...validValues);
      const range = max - min || 1; // Evita divisione per zero

      // Padding interno
      const padding = 2;
      const chartWidth = width - padding * 2;
      const chartHeight = height - padding * 2;

      // Genera i punti della linea
      const points = validValues.map((value, index) => {
        const x = padding + (index / (validValues.length - 1 || 1)) * chartWidth;
        // Inverti Y perché SVG ha origine in alto a sinistra
        const y = padding + chartHeight - ((value - min) / range) * chartHeight;
        return `${x},${y}`;
      });

      const pathData = `M ${points.join(' L ')}`;

      return (
        <svg width={width} height={height} style={{ display: 'block' }}>
          <polyline
            points={points.join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    },
    []
  );

  // Periodo di ~12 mesi non allineato all'anno solare (es. 07/2023-06/2024): una sola riga, dati sommati su tutti gli anni nel periodo
  const singlePeriodInfo = React.useMemo((): {
    label: string;
    yearsInPeriod: number[];
  } | null => {
    if (granularity !== 'year') return null;
    const from = dayjs(periodFrom);
    const to = dayjs(periodTo);
    const monthsDiff = to.diff(from, 'month', true);
    if (monthsDiff < 11 || monthsDiff > 13) return null;
    const fromIsStartOfYear = from.date() === 1 && from.month() === 0;
    const toIsEndOfYear = to.date() === to.endOf('month').date() && to.month() === 11;
    if (fromIsStartOfYear && toIsEndOfYear) return null;
    const yearFrom = from.year();
    const yearTo = to.year();
    const yearsInPeriod: number[] = [];
    for (let y = yearFrom; y <= yearTo; y++) yearsInPeriod.push(y);
    return {
      label: `${from.format('MM/YYYY')}-${to.format('MM/YYYY')}`,
      yearsInPeriod,
    };
  }, [periodFrom, periodTo, granularity]);

  const dateBuckets = React.useMemo(() => {
    const dates: Date[] = [];

    if (singlePeriodInfo) {
      dates.push(new Date(singlePeriodInfo.yearsInPeriod[0], 0, 1));
      return dates;
    }

    if (granularity === 'hour') {
      // Per la granularità 'hour', generiamo 24 ore per ogni giorno nel periodo
      const startDay = dayjs(periodFrom).startOf('day');
      const endDay = dayjs(periodTo).endOf('day');
      let currentDay = startDay;

      while (currentDay.isBefore(endDay) || currentDay.isSame(endDay, 'day')) {
        // Genera 24 ore per questo giorno (00:00 - 23:00)
        for (let hour = 0; hour < 24; hour++) {
          const hourDate = currentDay.hour(hour).minute(0).second(0).millisecond(0);
          dates.push(hourDate.toDate());
        }
        currentDay = currentDay.add(1, 'day');
      }
    } else {
      const start = dayjs(periodFrom).startOf(granularity);
      const end = dayjs(periodTo).endOf(granularity);
      let current = start;

      while (current.isBefore(end) || current.isSame(end, granularity)) {
        dates.push(current.toDate());
        current = current.add(1, granularity);
      }
    }

    return dates;
  }, [periodFrom, periodTo, granularity, singlePeriodInfo]);

  const formatDate = React.useCallback(
    (date: Date) => dayjs(date).format(GRANULARITY_FORMATS[granularity]),
    [granularity]
  );

  const resolvedValueProps: ResolvedValueProp[] = React.useMemo(() => {
    if (valuesProps && valuesProps.length > 0) {
      return valuesProps.map((prop, index) => ({
        field: prop.field,
        label: prop.label || `Valore ${index + 1}`,
        isPrimary: prop.isPrimary ?? false,
        editable: prop.editable ?? true,
        decimalPlaces: prop.decimalPlaces,
        getValue:
          prop.getValue ??
          ((valueObj: BaseData | null) =>
            valueObj ? ((valueObj as any)[prop.field] ?? null) : null),
      }));
    }

    return [
      {
        field: valueProperty,
        label: 'Valore',
        isPrimary: true,
        editable: true,
        getValue: (valueObj: BaseData | null) =>
          valueObj ? ((valueObj as any)[valueProperty] ?? null) : null,
      },
    ];
  }, [valuesProps, valueProperty]);

  const primaryValuePropIndex = React.useMemo(() => {
    const idx = resolvedValueProps.findIndex(prop => prop.isPrimary);
    return idx >= 0 ? idx : 0;
  }, [resolvedValueProps]);

  // Sort entities by sort key if provided
  const sortedEntities = React.useMemo(() => {
    if (!getEntitySortKey) {
      return entities;
    }

    return [...entities].sort((a, b) => {
      const keyA = getEntitySortKey(a);
      const keyB = getEntitySortKey(b);

      // Handle null/undefined values
      if (keyA == null && keyB == null) return 0;
      if (keyA == null) return 1; // null values go to the end
      if (keyB == null) return -1;

      // Compare values
      if (typeof keyA === 'number' && typeof keyB === 'number') {
        return keyA - keyB;
      }

      // String comparison
      return String(keyA).localeCompare(String(keyB));
    });
  }, [entities, getEntitySortKey]);

  // Ordine "naturale" delle entità (es. ordinate per index di backend / getEntitySortKey)
  const defaultColumnOrder = React.useMemo(
    () => sortedEntities.map(e => `entity-${e.id}`),
    [sortedEntities]
  );

  // Riconcilia columnOrder quando cambia l'elenco delle entità (nuova/eliminata), mantenendo
  // l'ordine scelto dall'utente per quelle ancora presenti. Senza questa riconciliazione, un
  // cambio di numero di entità (es. aggiunta di un secondo PODp) disallineava la lunghezza di
  // columnOrder rispetto a sortedEntities e le frecce di spostamento diventavano inattive.
  // In modalità "controlled" la riconciliazione è responsabilità del chiamante (che condivide
  // lo stesso ordine fra più tabelle collegate), quindi qui viene saltata.
  React.useEffect(() => {
    if (isColumnOrderControlled) return;
    setInternalColumnOrder(prevOrder => {
      const reconciled = reconcileColumnOrder(prevOrder, defaultColumnOrder);
      const unchanged =
        reconciled.length === prevOrder.length &&
        reconciled.every((id, i) => id === prevOrder[i]);
      return unchanged ? prevOrder : reconciled;
    });
  }, [defaultColumnOrder, isColumnOrderControlled]);

  // Determine entity order: use columnOrder if available and valid, otherwise use sortedEntities order
  const orderedEntities = React.useMemo(() => {
    if (columnOrder.length === 0) {
      return sortedEntities;
    }
    // Create a map for quick lookup
    const entityMap = new Map(sortedEntities.map(e => [`entity-${e.id}`, e]));
    // Order entities based on columnOrder
    const ordered = columnOrder
      .map(field => entityMap.get(field))
      .filter((e): e is (typeof sortedEntities)[0] => e !== undefined);
    // Se columnOrder non è ancora stato riconciliato con sortedEntities (es. nel singolo render
    // prima che l'effetto sopra sia applicato), usa sortedEntities per non perdere entità in
    // visualizzazione: l'effetto correggerà columnOrder al render successivo.
    return ordered.length === sortedEntities.length ? ordered : sortedEntities;
  }, [sortedEntities, columnOrder]);

  // Handle column move left
  const handleMoveColumnLeft = React.useCallback((entityId: string) => {
    updateColumnOrder(prevOrder => {
      const newOrder = [...prevOrder];
      const fieldName = `entity-${entityId}`;
      const currentIndex = newOrder.indexOf(fieldName);
      // Non permettere di scavalcare la colonna "Totale" (deve restare la prima colonna)
      if (currentIndex > 0 && newOrder[currentIndex - 1] !== 'entity-__TOTAL__') {
        // Swap with previous
        [newOrder[currentIndex - 1], newOrder[currentIndex]] = [
          newOrder[currentIndex],
          newOrder[currentIndex - 1],
        ];
      }
      return newOrder;
    });
  }, [updateColumnOrder]);

  // Handle column move right
  const handleMoveColumnRight = React.useCallback((entityId: string) => {
    updateColumnOrder(prevOrder => {
      const newOrder = [...prevOrder];
      const fieldName = `entity-${entityId}`;
      const currentIndex = newOrder.indexOf(fieldName);
      if (currentIndex < newOrder.length - 1) {
        // Swap with next
        [newOrder[currentIndex], newOrder[currentIndex + 1]] = [
          newOrder[currentIndex + 1],
          newOrder[currentIndex],
        ];
      }
      return newOrder;
    });
  }, [updateColumnOrder]);

  const valueLookup = React.useMemo(() => {
    const map = new Map<string, ValueWithEntityReference<BaseData>>();
    values.forEach(entry => {
      if (!entry.entityId) return;
      const entryDate = dayjs(entry.date ?? entry.month);
      let dateKey: number;

      if (granularity === 'hour') {
        // Per le ore, usiamo l'ora esatta (senza minuti/secondi)
        dateKey = entryDate.startOf('hour').valueOf();
      } else {
        dateKey = entryDate.startOf(granularity).valueOf();
      }
      map.set(`${dateKey}::${entry.entityId}`, entry);
    });
    return map;
  }, [values, granularity]);

  // Helper function to build grid rows
  const buildGridRows = React.useCallback((): GridRowsProp => {
    const hasComplexValues = resolvedValueProps.length > 1;

    return dateBuckets.flatMap((date, dateIndex) => {
      const dateKey = dayjs(date).startOf(granularity).valueOf();
      const dateLabel =
        singlePeriodInfo && dateIndex === 0 ? singlePeriodInfo.label : formatDate(date);

      return resolvedValueProps.map((valueProp, valuePropIndex) => {
        // Use old format for single values (backward compatibility), new format for complex values
        const rowId = hasComplexValues
          ? `date-${dateIndex}-prop-${valuePropIndex}`
          : `date-${dateIndex}`;

        const row: GridRowModel = {
          id: rowId,
          _date: date,
          _dateFormatted: dateLabel,
          _dateIndex: dateIndex,
          _valuePropIndex: valuePropIndex,
          _valuePropField: valueProp.field,
          _valuePropLabel: valueProp.label,
          _valuePropIsPrimary: valuePropIndex === primaryValuePropIndex,
          _valuePropEditable: valueProp.editable !== false,
        };

        sortedEntities.forEach(entity => {
          const entityKey = entity.id || (entity as any).code;
          let valueEntry: ValueWithEntityReference<BaseData> | undefined;
          let valueObj: BaseData | null = null;

          if (singlePeriodInfo && dateIndex === 0 && singlePeriodInfo.yearsInPeriod.length > 0) {
            const entries: ValueWithEntityReference<BaseData>[] = [];
            singlePeriodInfo.yearsInPeriod.forEach(year => {
              const yKey = dayjs(new Date(year, 0, 1)).startOf(granularity).valueOf();
              const e = entityKey ? valueLookup.get(`${yKey}::${entityKey}`) : undefined;
              if (e) entries.push(e);
            });
            if (entries.length > 0) {
              valueObj = aggregateValueObjects(entries.map(e => e.value as BaseData | null));
              valueEntry = entries[entries.length - 1];
            }
          } else {
            valueEntry =
              (entityKey && valueLookup.get(`${dateKey}::${entityKey}`)) || undefined;
            valueObj = (valueEntry?.value ?? null) as BaseData | null;
          }

          const cellValue = valueProp.getValue(valueObj);

          const safeCellValue =
            typeof cellValue === 'number' && !Number.isFinite(cellValue) ? null : cellValue ?? null;
          row[`entity-${entity.id}`] = safeCellValue;
          row[`_entity-${entity.id}-valueObj`] = valueObj;
          row[`_entity-${entity.id}-valueId`] =
            (valueObj as any)?.id ?? (valueEntry as any)?.valueId ?? null;
          row[`_entity-${entity.id}-entity`] = entity;
        });

        return row;
      });
    });
  }, [
    dateBuckets,
    singlePeriodInfo,
    sortedEntities,
    resolvedValueProps,
    formatDate,
    primaryValuePropIndex,
    valueLookup,
    granularity,
  ]);

  // Build the matrix: rows (dates * valueProps) x columns (entities)
  // Use state instead of memo so we can update it directly
  const [gridRows, setGridRows] = React.useState<GridRowsProp>(() => buildGridRows());

  // Update gridRows when dependencies change
  React.useEffect(() => {
    setGridRows(buildGridRows());
  }, [buildGridRows]);

  // Build columns
  const resolvedDateColumnLabel = dateColumnLabel || GRANULARITY_LABELS[granularity];

  const columns = React.useMemo<GridColDef[]>(() => {
    const currentDateHeader = isHeaderExpanded
      ? dateColumnHeaderExpanded || dateColumnHeader
      : dateColumnHeader;

    const cols: GridColDef[] = [
      {
        field: '_dateFormatted',
        headerName: resolvedDateColumnLabel,
        width: widthColDate,
        editable: false,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        headerClassName: 'sticky-date-column',
        renderCell: params => {
          const row = params.row as GridRowModel;
          const valuePropIndex = row._valuePropIndex as number;
          const resolvedValuePropsLength = resolvedValueProps.length;

          // Se c'è un renderDateCell personalizzato e non ci sono valueProps multipli, usalo
          if (renderDateCell && resolvedValuePropsLength === 1) {
            return renderDateCell({
              value: params.value as string,
              date: row._date as Date,
              row: row,
            });
          }

          // Se ci sono più valueProps (dati complessi), mostra l'anno solo sulla prima riga
          if (resolvedValuePropsLength > 1) {
            if (valuePropIndex === 0) {
              // Prima riga: mostra l'anno (verrà centrato verticalmente tramite CSS)
              return params.value;
            }
            // Altre righe: cella completamente vuota
            return '';
          }
          // Se non ci sono valueProps multipli, comportamento normale
          return params.value;
        },
        cellClassName: params => {
          const row = params.row as GridRowModel;
          const valuePropIndex = row._valuePropIndex as number;
          const resolvedValuePropsLength = resolvedValueProps.length;
          const classes: string[] = ['sticky-date-column'];

          if (resolvedValuePropsLength > 1) {
            if (valuePropIndex === 0) {
              classes.push('merged-date-cell-first');
            } else {
              classes.push('merged-date-cell-hidden');
            }
            // Aggiungi classe per bordo separatore se è l'ultima riga di un anno
            const isLastRowOfYear = valuePropIndex === resolvedValuePropsLength - 1;
            if (isLastRowOfYear) {
              classes.push('year-separator-row');
            }
          }
          return classes.join(' ');
        },
        renderHeader:
          currentDateHeader || dateColumnHeaderExpanded
            ? () => (
                <Box
                  sx={{
                    width: '100%',
                    height: isHeaderExpanded ? '100%' : '36px',
                    minHeight: isHeaderExpanded ? '100%' : '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: disableHeaderExpand ? 'center' : 'space-between',
                    py: isHeaderExpanded ? 1 : 0,
                  }}
                >
                  <Box sx={{ flex: 1 }}>{currentDateHeader}</Box>
                  {(dateColumnHeader || dateColumnHeaderExpanded) && !disableHeaderExpand && (
                    <IconButton
                      size="small"
                      onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
                      sx={{ p: 0.5, ml: 1, alignSelf: 'center' }}
                    >
                      {isHeaderExpanded ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </Box>
              )
            : undefined,
      },
    ];

    // Add "Dato" column only if there are multiple value props (complex values)
    if (resolvedValueProps.length > 1) {
      cols.push({
        field: '_valuePropLabel',
        headerName: 'Dato',
        width: 200,
        editable: false,
        sortable: false,
        headerClassName: 'sticky-data-column',
        cellClassName: params => {
          const row = params.row as GridRowModel;
          const valuePropIndex = row._valuePropIndex as number;
          const resolvedValuePropsLength = resolvedValueProps.length;
          const classes: string[] = ['sticky-data-column'];
          // Aggiungi classe per bordo separatore se è l'ultima riga di un anno
          const isLastRowOfYear = valuePropIndex === resolvedValuePropsLength - 1;
          if (isLastRowOfYear) {
            classes.push('year-separator-row');
          }
          return classes.join(' ');
        },
        renderHeader: () => (
          <Box
            sx={{
              width: '100%',
              height: isHeaderExpanded ? '100%' : '36px',
              minHeight: isHeaderExpanded ? '100%' : '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: isHeaderExpanded ? 1 : 0,
            }}
          >
            Dati
          </Box>
        ),
        renderCell: renderDataColumnCell
          ? params => renderDataColumnCell({ row: params.row as GridRowModel })
          : params => {
              const row = params.row as GridRowModel;
              return (
                <Box sx={{ width: '100%', pl: row._valuePropIndex > 0 ? 2 : 0 }}>
                  {row._valuePropLabel}
                </Box>
              );
            },
      });
    }

    // Add a column for each entity (in orderedEntities order)
    orderedEntities.forEach(entity => {
      // Use entity.id as the field name key
      const fieldName = `entity-${entity.id}`;
      const label = getEntityLabel(entity);

      // Collect monthly values for this entity
      // IMPORTANT: When using complex values (valuesProps), only the PRIMARY value is used for the chart
      const monthlyValues = dateBuckets.map(date => {
        const dateKey = dayjs(date).startOf(granularity).valueOf();
        const entityKey = entity.id || (entity as any).code;
        const valueEntry = (entityKey && valueLookup.get(`${dateKey}::${entityKey}`)) || undefined;
        const valueObj = (valueEntry?.value ?? null) as BaseData | null;
        // Extract only the primary value (marked with isPrimary: true) for the chart
        const primaryValue = resolvedValueProps[primaryValuePropIndex]?.getValue(valueObj) ?? null;
        return {
          month: date,
          value: primaryValue,
        };
      });

      // Determine which header renderer to use
      const currentEntityHeader = isHeaderExpanded
        ? renderEntityHeaderExpanded || renderEntityHeader
        : renderEntityHeader;

      cols.push({
        field: fieldName,
        headerName: label,
        width: widthCol,
        editable: !readOnly,
        type: 'number',
        align: 'center',
        sortable: false,
        headerAlign: 'center',
        cellClassName: params => {
          const row = params.row as GridRowModel;
          const hasComplexValues = resolvedValueProps.length > 1;
          if (!hasComplexValues) return '';
          // Aggiungi classe per bordo inferiore se è l'ultima riga di un anno
          const isLastRowOfYear = row._valuePropIndex === resolvedValueProps.length - 1;
          return isLastRowOfYear ? 'year-separator-row' : '';
        },
        renderHeader: currentEntityHeader
          ? () => {
              const entityIndex = orderedEntities.findIndex(e => e.id === entity.id);
              const previousEntity = entityIndex > 0 ? orderedEntities[entityIndex - 1] : undefined;
              // Non permettere di scavalcare la colonna "Totale" (deve restare la prima colonna)
              const canMoveLeft = entityIndex > 0 && previousEntity?.id !== '__TOTAL__';
              const canMoveRight = entityIndex < orderedEntities.length - 1;
              // Don't show ordering arrows for "Totale" column
              const isOrderable = entity.id !== '__TOTAL__';

              return (
                <Box
                  sx={{
                    width: '100%',
                    height: isHeaderExpanded ? 'auto' : '36px',
                    minHeight: isHeaderExpanded ? 'auto' : '36px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: isHeaderExpanded ? 'flex-start' : 'center',
                    py: isHeaderExpanded ? 1 : 0,
                    position: 'relative',
                  }}
                >
                  {isHeaderExpanded && isOrderable && (
                    <>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          display: 'flex',
                          gap: 0.5,
                          zIndex: 1,
                        }}
                      >
                        <IconButton
                          size="small"
                          disabled={!canMoveLeft}
                          onClick={e => {
                            e.stopPropagation();
                            handleMoveColumnLeft(entity.id!);
                          }}
                          sx={{
                            p: 0.25,
                            opacity: canMoveLeft ? 0.7 : 0.3,
                            '&:hover': { opacity: canMoveLeft ? 1 : 0.3 },
                          }}
                        >
                          <ArrowLeftIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          display: 'flex',
                          gap: 0.5,
                          zIndex: 1,
                        }}
                      >
                        <IconButton
                          size="small"
                          disabled={!canMoveRight}
                          onClick={e => {
                            e.stopPropagation();
                            handleMoveColumnRight(entity.id!);
                          }}
                          sx={{
                            p: 0.25,
                            opacity: canMoveRight ? 0.7 : 0.3,
                            '&:hover': { opacity: canMoveRight ? 1 : 0.3 },
                          }}
                        >
                          <ArrowRightIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </>
                  )}
                  <Box sx={{ width: '100%' }}>{currentEntityHeader(entity, monthlyValues)}</Box>
                  {isHeaderExpanded && (
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        opacity: 0.7,
                      }}
                    >
                      <MiniLineChart values={monthlyValues} width={180} height={20} />
                    </Box>
                  )}
                </Box>
              );
            }
          : undefined,
        renderCell: renderCell
          ? params => {
              const row = params.row as GridRowModel;
              const date = row._date as Date;
              const valueObj = row[`_entity-${entity.id}-valueObj`] as any | null;
              const value = params.value as number | string | null | undefined;
              const valueProp = resolvedValueProps[row._valuePropIndex as number];

              return (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: row._valuePropIsPrimary ? 'context-menu' : 'default',
                  }}
                  onContextMenu={(e: React.MouseEvent) => {
                    if (!row._valuePropIsPrimary) return;
                    e.preventDefault();
                    const rowId = params.row.id as string;
                    const colIndex = orderedEntities.findIndex(e => e.id === entity.id);
                    setContextMenu({
                      mouseX: e.clientX - 2,
                      mouseY: e.clientY - 4,
                      rowId,
                      field: params.field,
                      rowIndex: row._dateIndex as number,
                      colIndex,
                    });
                  }}
                >
                  {renderCell({
                    value,
                    month: date,
                    entityId: entity.id!,
                    entity,
                    valueObj,
                    valueProp,
                  })}
                </Box>
              );
            }
          : params => {
              const row = params.row as GridRowModel;
              const date = row._date as Date;
              const valueProp = resolvedValueProps[row._valuePropIndex as number];
              const fmtOpts =
                valueProp?.decimalPlaces != null
                  ? {
                      maximumFractionDigits: valueProp.decimalPlaces,
                      minimumFractionDigits: valueProp.decimalPlaces,
                    }
                  : undefined;
              const displayValue =
                typeof params?.value === 'number' ||
                (typeof params.value === 'string' && params.value !== '')
                  ? formatNumberItalian(params.value as any, fmtOpts)
                  : (params.value ?? '');

              return (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: row._valuePropIsPrimary ? 'context-menu' : 'default',
                  }}
                  onContextMenu={(e: React.MouseEvent) => {
                    if (!row._valuePropIsPrimary) return;
                    e.preventDefault();
                    const rowId = params.row.id as string;
                    const colIndex = orderedEntities.findIndex(e => e.id === entity.id);
                    setContextMenu({
                      mouseX: e.clientX - 2,
                      mouseY: e.clientY - 4,
                      rowId,
                      field: params.field,
                      rowIndex: row._dateIndex as number,
                      colIndex,
                    });
                  }}
                >
                  {displayValue}
                </Box>
              );
            },
      });
    });

    return cols;
  }, [
    orderedEntities,
    sortedEntities,
    handleMoveColumnLeft,
    handleMoveColumnRight,
    getEntityLabel,
    renderCell,
    renderDateCell,
    renderEntityHeader,
    renderEntityHeaderExpanded,
    entityColumnWidth,
    entityColumnWidthExpanded,
    widthCol,
    widthColDate,
    dateBuckets,
    values,
    getValue,
    dateColumnHeader,
    dateColumnHeaderExpanded,
    MiniLineChart,
    isHeaderExpanded,
    resolvedDateColumnLabel,
    granularity,
    resolvedValueProps,
    valueLookup,
    primaryValuePropIndex,
    readOnly,
    renderDataColumnCell,
  ]);

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Funzione per esportare la tabella in CSV
  const handleExportCSV = React.useCallback(() => {
    // Prepara gli header: data + colonne per ogni entità
    const headers: string[] = [resolvedDateColumnLabel];
    orderedEntities.forEach(entity => {
      const entityLabel = getEntityLabel ? getEntityLabel(entity) : entity.id || '';
      resolvedValueProps.forEach(valueProp => {
        headers.push(`${entityLabel} - ${valueProp.label}`);
      });
    });

    // Prepara i dati
    const csvRows: string[] = [];
    dateBuckets.forEach((date, dateIndex) => {
      const row: string[] = [];

      // Aggiungi la data formattata
      const dateFormatted = dayjs(date).format(GRANULARITY_FORMATS[granularity]);
      row.push(dateFormatted);

      // Per ogni entità e ogni value prop, aggiungi il valore
      orderedEntities.forEach(entity => {
        resolvedValueProps.forEach((valueProp, valuePropIndex) => {
          const fieldName = `entity-${entity.id}`;
          // Trova la riga corrispondente a questa data e value prop
          const rowData = gridRows.find(
            r => r._dateIndex === dateIndex && r._valuePropIndex === valuePropIndex
          );

          if (rowData) {
            const value = rowData[fieldName];
            // Usa la virgola come separatore decimale (coerente con il separatore di campo ';'),
            // così Excel in locale italiano non perde i decimali in fase di apertura.
            const displayValue =
              value !== null && value !== undefined && value !== ''
                ? (toCSVNumericString(value) ?? String(value))
                : '';
            row.push(displayValue);
          } else {
            row.push('');
          }
        });
      });

      csvRows.push(
        row
          .map(v => (v.includes(';') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v))
          .join(';')
      );
    });

    const csvContent = [headers.join(';'), ...csvRows].join('\n');
    downloadCSV(csvContent, 'export');
    handleCloseContextMenu();
  }, [
    gridRows,
    orderedEntities,
    resolvedValueProps,
    dateBuckets,
    granularity,
    resolvedDateColumnLabel,
    getEntityLabel,
    handleCloseContextMenu,
  ]);

  const handlePasteFromClipboard = async () => {
    if (!contextMenu) return;

    try {
      const clipboardText = await navigator.clipboard.readText();
      const result = apiRef.current?.getRowModels
        ? await (ref as any)?.current?.importFromClipboard(
            clipboardText,
            contextMenu.rowIndex,
            contextMenu.colIndex
          )
        : null;
      if (result?.success) {
        // Success is handled by the importFromClipboard function
        handleCloseContextMenu();
      } else {
        toast.error(`Errore durante l'incolla dei dati.`);
      }
    } catch (error: any) {
      toast.error(`Errore durante la lettura dei dati`);
      handleCloseContextMenu();
    }
  };

  // Get the ref's importFromClipboard method
  const gridRef = React.useRef<CrossEntityDateCrudDataGridHandle | null>(null);
  React.useImperativeHandle(ref, () => {
    const handle = {
      getTableData: (): CrossEntityTableDataItem[] => {
        const data: CrossEntityTableDataItem[] = [];
        let rowsToProcess: readonly GridRowModel[] = gridRows;

        if (apiRef.current) {
          try {
            const rowModels = apiRef.current.getRowModels();
            if (rowModels && rowModels.size > 0) {
              rowsToProcess = Array.from(rowModels.values());
            }
          } catch (error) {
            console.warn('Could not get row models from DataGrid API:', error);
          }
        }

        (rowsToProcess as GridRowModel[]).forEach((row: GridRowModel) => {
          const date = row._date as Date;
          orderedEntities.forEach(entity => {
            const fieldName = `entity-${entity.id}`;
            const value = row[fieldName] as number | string | null | undefined;
            const valueId = row[`_entity-${entity.id}-valueId`] as string | null;
            const originalValueObj = row[`_entity-${entity.id}-valueObj`] as any | null;

            // Include sempre il valore, anche se è null/vuoto, se:
            // 1. C'è un valueId (record esistente) - per permettere la cancellazione
            // 2. Il valore è stato modificato (non null/undefined/vuoto)
            // 3. C'era un valore originale e ora è vuoto (cancellazione)
            const shouldInclude =
              valueId !== null || // Record esistente (permette cancellazione)
              (value !== null && value !== undefined && value !== ''); // Nuovo valore non vuoto

            if (shouldInclude) {
              const entityId = entity.id;
              data.push({
                month: date,
                entityId: entityId || '',
                value: value !== '' ? (value as number | string | null) : null, // Converti stringa vuota in null
                valueId: valueId || null,
                entity: entity as BaseEntity,
              });
            }
          });
        });

        return data;
      },
      getApiRef: () => apiRef,
      goToDate: (date: Date) => {
        if (!pageSize || !periodFrom || !periodTo) {
          return;
        }

        // Usa dayjs per normalizzare correttamente le date
        const startDate = dayjs(periodFrom).startOf('day');
        const endDate = dayjs(periodTo).startOf('day');
        const targetDate = dayjs(date).startOf('day');

        // Verifica che la data sia nel range
        if (targetDate.isBefore(startDate) || targetDate.isAfter(endDate)) {
          toast.error('La data selezionata non è nel periodo valido');
          return;
        }

        // Calcola la differenza in giorni usando dayjs
        const diffDays = targetDate.diff(startDate, 'day');

        // Calcola la pagina (ogni pagina = 1 giorno = 24 righe)
        const targetPage = diffDays;

        // Aggiorna la paginazione
        setPaginationModel({ page: targetPage, pageSize });
      },
      importFromClipboard: async (
        clipboardData: string,
        startRowIndex?: number,
        startColIndex?: number
      ): Promise<{ success: boolean; error?: string }> => {
        try {
          // Come sopra: preserva i TAB che rappresentano celle vuote
          let lines = clipboardData.split(/\r?\n/);
          if (lines.length > 0 && lines[lines.length - 1] === '') {
            lines = lines.slice(0, -1);
          }

          if (lines.length === 0) {
            return { success: false, error: 'Nessun dato trovato nella clipboard' };
          }

          // Mostra un messaggio se ci sono molti dati da processare
          if (lines.length > 500) {
            toast.loading(`Elaborazione di ${lines.length} righe in corso...`, {
              id: 'paste-loading',
            });
          }

          const parsedData = lines.map(line => {
            const columns = line.includes('\t')
              ? line.split('\t') // tab tab => cella vuota preservata
              : line.split(/\s{2,}/).filter(col => col.trim().length > 0);

            return columns.map(col => {
              const cleaned = col.trim().replace(/\./g, '').replace(/\s/g, '');
              const num = parseFloat(cleaned.replace(',', '.'));
              return isNaN(num) ? null : num;
            });
          });

          if (startRowIndex !== undefined && startColIndex !== undefined) {
            // Aggiorna direttamente gridRows invece di usare apiRef
            setGridRows(currentRows => {
              const newRows = [...currentRows];

              parsedData.forEach((rowData, dataRowIndex) => {
                const targetRowIndex = startRowIndex + dataRowIndex;
                if (targetRowIndex >= dateBuckets.length) {
                  return;
                }

                const rowId = `date-${targetRowIndex}`;
                const rowIndex = newRows.findIndex(r => r.id === rowId);

                if (rowIndex !== -1) {
                  const updatedRow = { ...newRows[rowIndex] };
                  rowData.forEach((value, dataColIndex) => {
                    const targetColIndex = startColIndex + dataColIndex;
                    // IMPORTANTE: usa `orderedEntities` (ordine visualizzato, lo stesso da cui
                    // viene calcolato `colIndex` nel menu contestuale), non `sortedEntities`
                    // (ordine "naturale" lato backend). Dopo uno spostamento colonna i due ordini
                    // divergono: usare `sortedEntities` qui faceva incollare i dati sulla colonna
                    // sbagliata (quella adiacente a quella su cui si era fatto click destro).
                    if (targetColIndex < orderedEntities.length) {
                      const entity = orderedEntities[targetColIndex];
                      const fieldName = `entity-${entity.id}`;
                      updatedRow[fieldName] = value;
                    }
                  });
                  newRows[rowIndex] = updatedRow;
                }
              });

              return newRows;
            });

            toast.dismiss('paste-loading');
            if (parsedData.length > 500) {
              toast.success(`Elaborate ${parsedData.length} righe con successo`);
            }

            return { success: true };
          }

          // Original full table paste logic
          const expectedRows = dateBuckets.length;
          const expectedCols = orderedEntities.length;

          if (parsedData.length !== expectedRows) {
            return {
              success: false,
              error: `Numero di righe non corrispondente. Attese: ${expectedRows}, trovate: ${parsedData.length}`,
            };
          }

          const firstRowCols = parsedData[0]?.length || 0;
          if (parsedData.some(row => row.length !== firstRowCols)) {
            return {
              success: false,
              error: 'Le righe hanno un numero diverso di colonne',
            };
          }

          if (firstRowCols !== expectedCols) {
            return {
              success: false,
              error: `Numero di colonne non corrispondente. Attese: ${expectedCols}, trovate: ${firstRowCols}`,
            };
          }

          // Aggiorna direttamente gridRows invece di usare apiRef
          setGridRows(currentRows => {
            const newRows = [...currentRows];

            parsedData.forEach((rowData, rowIndex) => {
              const rowId = `date-${rowIndex}`;
              const rowArrayIndex = newRows.findIndex(r => r.id === rowId);

              if (rowArrayIndex !== -1) {
                const updatedRow = { ...newRows[rowArrayIndex] };
                orderedEntities.forEach((entity, colIndex) => {
                  const fieldName = `entity-${entity.id}`;
                  updatedRow[fieldName] = rowData[colIndex];
                });
                newRows[rowArrayIndex] = updatedRow;
              }
            });

            return newRows;
          });

          toast.dismiss('paste-loading');
          if (parsedData.length > 500) {
            toast.success(`Elaborate ${parsedData.length} righe con successo`);
          }

          return { success: true };
        } catch (error: any) {
          console.error('Error importing from clipboard:', error);
          toast.dismiss('paste-loading');
          return { success: false, error: error?.message || "Errore durante l'importazione" };
        }
      },
      getEntityOrder: (): string[] => {
        // Return entity IDs in the current column order, excluding "__TOTAL__"
        return columnOrder
          .map((field: string) => field.replace('entity-', ''))
          .filter((id: string) => Boolean(id) && id !== '__TOTAL__') as string[];
      },
    };
    gridRef.current = handle as any;
    return handle;
  }, [
    gridRows,
    orderedEntities,
    apiRef,
    dateBuckets,
    primaryValuePropIndex,
    resolvedValueProps,
    pageSize,
    periodFrom,
    periodTo,
    setPaginationModel,
    columnOrder,
  ]);

  return (
    <Box
      sx={{
        width: '100%',
        height,
        ...(disableHorizontalScroll && { overflow: 'visible' }),
      }}
    >
      <DataGrid
        apiRef={apiRef}
        rows={gridRows}
        columns={columns}
        getRowHeight={() => 36}
        columnHeaderHeight={36}
        isCellEditable={params => {
          if (readOnly) return false;
          if (!params.field.startsWith('entity-')) {
            return false;
          }
          const row = params.row as GridRowModel;
          return row._valuePropEditable !== false;
        }}
        editMode={readOnly ? undefined : 'cell'}
        loading={loading}
        autoHeight={false}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableColumnSorting
        disableRowSelectionOnClick
        columnBufferPx={10000}
        hideFooter={!pageSize}
        paginationModel={pageSize ? paginationModel : undefined}
        onPaginationModelChange={pageSize ? setPaginationModel : undefined}
        processRowUpdate={processRowUpdate}
        onCellEditStop={params => {
          if (onCellEditStop) {
            onCellEditStop();
          }
          return params;
        }}
        slots={{
          footer:
            pageSize && dateNavigation
              ? () => (
                  <GridFooterContainer>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ flex: 1 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {renderCustomFooter && renderCustomFooter()}
                        <GridPagination
                          slotProps={{
                            labelDisplayedRows: () => '', // Nasconde il numero di elementi
                          }}
                        />
                      </Box>
                    </Box>
                  </GridFooterContainer>
                )
              : undefined,
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'circular-progress',
            noRowsVariant: 'circular-progress',
          },
        }}
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          ...(disableHorizontalScroll && {
            overflow: 'visible',
            '& .MuiDataGrid-main': { overflowX: 'visible' },
            '& .MuiDataGrid-virtualScroller': { overflowX: 'visible' },
          }),
          '& .MuiDataGrid-cell.sticky-date-column': {
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: 'background.paper',
          },
          '& .MuiDataGrid-columnHeader.sticky-date-column': {
            position: 'sticky',
            left: 0,
            zIndex: 3,
            backgroundColor: 'background.paper',
          },
          '& .MuiDataGrid-cell.sticky-data-column': {
            position: 'sticky',
            left: `${widthColDate}px`,
            zIndex: 2,
            backgroundColor: 'background.paper',
          },
          '& .MuiDataGrid-columnHeader.sticky-data-column': {
            position: 'sticky',
            left: `${widthColDate}px`,
            zIndex: 3,
            backgroundColor: 'background.paper',
          },
          '& .MuiDataGrid-cell.merged-date-cell-first': {
            borderBottom: 'none',
            display: 'flex !important',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '156px !important', // 3 righe * 52px
            height: 'auto !important',
            color: 'text.primary',
            overflow: 'visible',
            zIndex: 1000,
          },
          '& .MuiDataGrid-cell.merged-date-cell-hidden': {
            borderTop: 'none',
            borderBottom: 'none',
            padding: 0,
            height: '52px', // Altezza standard di una riga
            minHeight: '52px',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-cell.merged-date-cell': {
            borderTop: 'none',
          },
          // Bordo orizzontale che separa un anno dall'altro (solo per valori complessi)
          // Include tutte le colonne: Anno, Dato e entità
          '& .MuiDataGrid-cell.year-separator-row': {
            borderBottom: '3px solid',
            borderBottomColor: 'divider',
          },
          // Bordo verticale tra la seconda colonna (Dato) e la terza colonna (prima entità)
          '& .MuiDataGrid-cell[data-field="_valuePropLabel"]': {
            borderRight: '3px solid',
            borderRightColor: 'divider',
            display: 'flex !important',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'visible',
            zIndex: 1,
          },
          '& .MuiDataGrid-columnHeader[data-field="_valuePropLabel"]': {
            borderRight: '3px solid',
            borderRightColor: 'divider',
            ...(isHeaderExpanded && {
              height: '100%',
              minHeight: '100% ',
              maxHeight: 'none',
            }),
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'block',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
          '& .MuiDataGrid-columnHeaders': {
            height: 'auto !important',
            minHeight: 'auto',
            alignItems: 'flex-start',
            '& > div:first-of-type': {
              height: 'auto !important',
              minHeight: 'auto',
              alignItems: 'flex-start',
            },
          },
          ...(isHeaderExpanded && {
            '& .MuiDataGrid-columnHeader': {
              height: 'auto !important',
            },
          }),
          '& .MuiDataGrid-columnHeaderTitle': {
            overflow: 'visible',
            whiteSpace: 'normal',
            lineHeight: '1.5',
          },
          '& .MuiDataGrid-columnHeaderRow': {
            height: 'auto !important',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: '1px solid',
            borderColor: 'divider',
            '&:last-of-type': {
              borderRight: 'none',
            },
          },
          '& .MuiDataGrid-cell': {
            borderRight: '1px solid',
            borderColor: 'divider',
            '&:last-of-type': {
              borderRight: 'none',
            },
          },
          // Centra verticalmente le celle della prima colonna (data)
          '& .MuiDataGrid-cell[data-field="_dateFormatted"]': {
            display: 'flex !important',
            alignItems: 'center',
            justifyContent: 'center',
          },
          // Quando l'header è espanso, la colonna data e la colonna Dato occupano tutta l'altezza
          '& .MuiDataGrid-columnHeader[data-field="_dateFormatted"]': {
            ...(isHeaderExpanded && {
              height: '100% !important',
              minHeight: '100% !important',
            }),
          },
          // Nasconde il testo "217–240 di 8784" nella paginazione
          '& .MuiTablePagination-displayedRows': {
            display: 'none',
          },
        }}
      />
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
        }
        slotProps={{
          root: {
            onContextMenu: (event: React.MouseEvent) => {
              event.preventDefault();
              handleCloseContextMenu();
            },
          },
        }}
      >
        <MenuItem onClick={handleExportCSV}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Esporta tabella in CSV</ListItemText>
        </MenuItem>
        {enablePaste && (
          <MenuItem onClick={handlePasteFromClipboard}>
            <ListItemIcon>
              <ContentPasteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Incolla</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
});

CrossEntityDateCrudDataGrid.displayName = 'CrossEntityDateCrudDataGrid';
